import { useState, useEffect } from "react";
import { AxiosError } from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import Table from "../../../core/common/dataTable/index";
import { SubjectTable } from "../../../core/data/interface"; //Les types des colones de notre tableau déclaré au préalable
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import EditSubjectModal from "./editSubjectModal";
import CreateSubjectModal from "./createSubjectModal";
import { useAcademicYear } from "../../../core/common/context/AcademicYearContext";
import { deleteSubject, getAllSubject } from "../../../api/pedagogicElement";
import TooltipOption from "./tooltipOption";
import InfosAlertYearDisabled from "../../../core/common/alerts/infosAlertYearDisabled";
import withPermission from "../../../core/common/autorisations/withPermission";
import {
  Action,
  buildPermission,
  Resource,
} from "../../../core/common/autorisations/permissionsMap";
import DashboardMenu from "../../user/admin/dashboardMenu";

const ProtectedBtnAddSubject = withPermission(
  (props: any) => (
    <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
      <TooltipOption updateData={props.reloadData} />
      <div className="mb-2">
        <button
          onClick={() => {
            props.setShowCreate(true);
          }}
          //   to="#"
          className="btn btn-primary d-flex align-items-center"
        >
          <i className="ti ti-square-rounded-plus me-2" />
          Nouvelle Matière
        </button>
      </div>
    </div>
  ),
  [buildPermission(Resource.SUBJECT, Action.CREATE)],
  "masquer",
);

// boutton protégée pour modifier une matiere
const ProtectedBtnUpdateSubject = withPermission(
  (props: any) => (
    <li>
      <Link
        className="dropdown-item rounded-1"
        to="#"
        onClick={() => {
          props.setEditSubject(props.record.id);
          props.setShowEdit(true);
        }}
      >
        <i className="ti ti-edit-circle me-2" />
        Modifier
      </Link>
    </li>
  ),
  [buildPermission(Resource.SUBJECT, Action.UPDATE)],
  "masquer",
);

// boutton protégée pour supprimer une matiere
const ProtectedBtnDeleteSubject = withPermission(
  (props: any) => (
    <li>
      <Link
        onClick={() => props.showConfirmationAlert(props.record)}
        className="dropdown-item rounded-1"
        to="#"
      >
        <i className="ti ti-trash-x me-2" />
        Supprimer
      </Link>
    </li>
  ),
  [buildPermission(Resource.SUBJECT, Action.DELETE)],
  "masquer",
);

const SubjectList = () => {
  const { selectedYear, setSelectedYear } = useAcademicYear();

  //Initialisation des state utilisés
  const [allSubject, setAllSubject] = useState([]);
  const [editSubject, setEditSubject] = useState("");
  const [isLoading, setIsLoading] = useState(true); // Pour le vrai chargement
  const [hasLoaded, setHasLoaded] = useState(false); // Pour éviter "Chargement..." quand il n'y a juste pas de données

  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);

  const [refreshKey, setRefreshKey] = useState(0);
  //Initialisation de sweetAlert
  const MySwal = withReactContent(Swal);

  // Les colonnes de notre tableau
  const columns = [
    {
      title: "CODE",
      dataIndex: "code",
      sorter: (a: SubjectTable, b: SubjectTable) =>
        a.code.length - b.code.length,
    },
    {
      title: "Libellé",
      dataIndex: "label",
      sorter: (a: SubjectTable, b: SubjectTable) =>
        a.label.length - b.label.length,
    },
    {
      title: "Date de création",
      dataIndex: "createdAt",
      render: (date: string) => {
        const d = new Date(date);
        return d.toLocaleDateString("fr-FR", {
          year: "numeric",
          month: "long",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });
      },
      sorter: (a: SubjectTable, b: SubjectTable) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    },
    {
      title: "Action",
      dataIndex: "action",
      render: (_: any, record: any) => (
        <>
          <div className="d-flex align-items-center">
            <div className="dropdown">
              <Link
                to="#"
                className="btn btn-white btn-icon btn-sm d-flex align-items-center justify-content-center rounded-circle p-0"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <i className="ti ti-dots-vertical fs-14" />
              </Link>
              <ul className="dropdown-menu dropdown-menu-right p-3">
                {/* modifier une matière */}
                <ProtectedBtnUpdateSubject
                  setEditSubject={setEditSubject}
                  record={record}
                  setShowEdit={setShowEdit}
                />

                {/* supprimer une matière */}
                <ProtectedBtnDeleteSubject
                  showConfirmationAlert={showConfirmationAlert}
                  record={record}
                />
              </ul>
            </div>
          </div>
        </>
      ),
    },
  ];

  //Traitement en alert de la demande de suppression de salle de classe
  const showConfirmationAlert = (subject: any) => {
    MySwal.fire({
      title: "Supprimer une matière ?",
      text: `Souhaitez-vous vraiment supprimer la matière ${subject.label} ?`,
      showCancelButton: true,
      // confirmButtonColor: "#00ff00",
      confirmButtonText: "Envoyer!",
      // cancelButtonColor: "#ff0000",
      cancelButtonText: "Annuler",
    }).then(async (result) => {
      if (result.isConfirmed) {
        // ── Affiche le spinner pendant la requête ──
        MySwal.fire({
          title: "Suppression en cours...",
          allowOutsideClick: false,
          allowEscapeKey: false,
          showConfirmButton: false,
          didOpen: () => MySwal.showLoading(),
        });
        try {
          //On lance la requête d'envoi de mail
          await deleteSubject(subject?.id);
          MySwal.fire({
            title: "Matière supprimée avec succès !",
            icon: "success",
            confirmButtonText: "OK",
          });

          reloadData(); //On reload la liste
        } catch (error) {
          const err = error as AxiosError<any>;
          //On affiche une alerte en cas d'erreur côté serveur
          MySwal.fire({
            title: "Erreur",
            text: err.response?.data?.message || err.response?.data?.error,
            icon: "error",
            confirmButtonText: "OK",
          });
        }
      }
    });
  };

  //Reload de la liste
  const reloadData = () => setRefreshKey((prev) => prev + 1);

  //On recupère la liste des Salles de classe en appelant sa fonction
  useEffect(() => {
    const fetchData = async () => {
      // On attend que selectedYear soit défini
      if (!selectedYear?.id) return;

      setHasLoaded(false);
      setIsLoading(true);

      try {
        const subjects = await getAllSubject(selectedYear?.id);
        setAllSubject(subjects.data);
      } catch (error: any) {
        // Si erreur (Toast Error)
        toast.error(
          error.message || "Une erreur s'est produit, Réessayer plus tard",
        );
      } finally {
        setIsLoading(false); //Les données ne sont plus en chargement
        setHasLoaded(true); //Les données ont été chargé avec succès
      }
    };

    fetchData();
  }, [refreshKey, selectedYear]);

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          {/* Page Header */}
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <DashboardMenu />
            {/* Boutton en entête de la page */}
            <ProtectedBtnAddSubject
              reloadData={reloadData}
              setShowCreate={setShowCreate}
            />
          </div>
          {/* /Page Header */}
          {!selectedYear?.statut && <InfosAlertYearDisabled />}
          {/* Tableau liste du personnel administratif */}
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
              <h4 className="mb-3">Liste des Matières</h4>
            </div>
            <div className="card-body p-0 py-3">
              {/* Staffs List (On affiche le tableau qui si les données son chargées même vide) */}
              {isLoading && !hasLoaded ? (
                <div className="text-center">
                  <div className="spinner-border" role="status"></div>
                  <span className="visually">Chargement des Données...</span>
                </div>
              ) : (
                <Table
                  columns={columns}
                  dataSource={allSubject}
                  Selection={true}
                />
              )}
              {/* /Staffs List */}
            </div>
          </div>
        </div>
        {/* /Page Header */}

        <EditSubjectModal
          show={showEdit}
          subjectID={editSubject}
          onClose={() => setShowEdit(false)}
          onSaved={reloadData}
        />
        <CreateSubjectModal
          show={showCreate}
          onClose={() => setShowCreate(false)}
          onSaved={reloadData}
        />
      </div>
    </>
  );
};

export default SubjectList;
